/**
 * HollerHub TimelineRenderer (Phase 1.7)
 * ───────────────────────────────────────
 * Shared, entity-agnostic Gantt-style timeline. Drops onto any asset or
 * planting detail page. Pulls events from the
 * /api/timeline/:entity_type/:entity_id endpoint server-side aggregator.
 *
 * Implementation notes:
 *  - No charting library; bars are absolutely positioned divs on a
 *    width-fixed track that scrolls horizontally when needed.
 *  - Pixel-per-day depends on zoom level: day=40, week=14, month=4, year=1.
 *  - Past events render at full opacity; events with is_projected=true at 50%.
 *  - Today marker is a 2px dashed vertical line in the clay token.
 *  - Filter chips toggle event types in/out client-side.
 *  - Click any bar to expand a detail panel below the timeline.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, Filter, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

export type TimelineEventType =
  | "maintenance"
  | "vet"
  | "vaccination"
  | "breeding"
  | "pasture_move"
  | "animal_care"
  | "planting"
  | "harvest"
  | "note";

export type TimelineEntityType =
  | "vehicle"
  | "animal"
  | "pasture"
  | "implement"
  | "tool"
  | "garden"
  | "planting";

export interface TimelineEvent {
  id: string;
  entity_type: TimelineEntityType;
  entity_id: string;
  start_date: string;
  end_date: string | null;
  type: TimelineEventType;
  title: string;
  status: string | null;
  color_token: string | null;
  source_id: string;
  source_table: string;
  is_projected: boolean;
  description?: string | null;
}

type ZoomLevel = "day" | "week" | "month" | "year";

const PX_PER_DAY: Record<ZoomLevel, number> = {
  day: 40,
  week: 14,
  month: 4,
  year: 1,
};

const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 32;
const TRACK_HEIGHT = ROW_HEIGHT * 4; // Up to 4 stacked lanes per type cluster

/**
 * Tailwind class strings per event type. Matches the spec color-coding table.
 * Maps each timeline event type to a brand-semantic palette per HollerHub
 * Brand Guidelines v1.1. Each entry uses 15%/30% alpha background +
 * full-strength foreground for AA contrast in light mode, and a 25% alpha
 * dark variant. Hover ring color comes from the same hue family.
 *
 *   maintenance   → Sky Muted  (info — service / mechanical work)
 *   vet           → Moss       (success — health intervention)
 *   vaccination   → Olive      (lifecycle care — planned secondary)
 *   breeding      → Clay       (reproductive accent — alert-adjacent)
 *   pasture_move  → Forest     (land use)
 *   animal_care   → Moss       (success — daily wellness)
 *   planting      → Olive      (lifecycle — secondary)
 *   harvest       → Burnt Orange (warm action — primary CTA color)
 *   note          → Surface Raised (neutral, context note)
 */
const TYPE_BAR_CLASS: Record<TimelineEventType, string> = {
  maintenance:  "bg-sky-muted/15 text-sky-muted border-sky-muted/40 dark:bg-sky-muted-dark/25 dark:text-sky-muted-dark dark:border-sky-muted-dark/40",
  vet:          "bg-moss/15 text-moss border-moss/40 dark:bg-moss-dark/25 dark:text-moss-dark dark:border-moss-dark/40",
  vaccination:  "bg-olive/15 text-olive border-olive/40 dark:bg-olive-dark/25 dark:text-olive-dark dark:border-olive-dark/40",
  breeding:     "bg-clay/15 text-clay border-clay/40 dark:bg-clay-dark/25 dark:text-clay-dark dark:border-clay-dark/40",
  pasture_move: "bg-forest/15 text-forest border-forest/40 dark:bg-forest-dark/25 dark:text-forest-dark dark:border-forest-dark/40",
  animal_care:  "bg-moss/15 text-moss border-moss/40 dark:bg-moss-dark/25 dark:text-moss-dark dark:border-moss-dark/40",
  planting:     "bg-olive/15 text-olive border-olive/40 dark:bg-olive-dark/25 dark:text-olive-dark dark:border-olive-dark/40",
  harvest:      "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/40 dark:bg-burnt-orange-dark/25 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/40",
  note:         "bg-surface-raised text-charcoal border-token-border dark:bg-surface-raised-dark dark:text-charcoal-dark dark:border-token-border-dark",
};

const TYPE_LABEL: Record<TimelineEventType, string> = {
  maintenance: "Maintenance",
  vet: "Vet",
  vaccination: "Vaccination",
  breeding: "Breeding",
  pasture_move: "Pasture move",
  animal_care: "Animal care",
  planting: "Planting",
  harvest: "Harvest",
  note: "Note",
};

const ALL_TYPES: TimelineEventType[] = [
  "maintenance", "vet", "vaccination", "breeding", "pasture_move",
  "animal_care", "planting", "harvest", "note",
];

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setUTCDate(out.getUTCDate() - out.getUTCDay());
  out.setUTCHours(0, 0, 0, 0);
  return out;
}

interface TimelineRendererProps {
  entityType: TimelineEntityType;
  entityId: string;
  /** Optional default zoom — defaults to "month" */
  defaultZoom?: ZoomLevel;
  /** Optional initial range; defaults to [today - 6mo, today + 6mo] */
  defaultRangeMonths?: number;
  /** When true (default), users can add inline annotations */
  allowAnnotations?: boolean;
  /** Optional callback after annotation is created — for parent refresh */
  onAnnotationCreated?: () => void;
  /**
   * Optional external refresh trigger. Increment from the host page
   * (e.g., after a cascade apply) to force the timeline to re-fetch.
   */
  refreshKey?: number;
}

export function TimelineRenderer({
  entityType,
  entityId,
  defaultZoom = "month",
  defaultRangeMonths = 12,
  allowAnnotations = true,
  onAnnotationCreated,
  refreshKey = 0,
}: TimelineRendererProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState<ZoomLevel>(defaultZoom);
  const [activeFilters, setActiveFilters] = useState<Set<TimelineEventType>>(
    new Set(ALL_TYPES)
  );
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);

  // Compute the visible range — symmetric window around today
  const { rangeStart, rangeEnd } = useMemo(() => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setUTCMonth(start.getUTCMonth() - defaultRangeMonths / 2);
    start.setUTCDate(1);
    const end = new Date(today);
    end.setUTCMonth(end.getUTCMonth() + defaultRangeMonths / 2);
    return { rangeStart: startOfMonth(start), rangeEnd: end };
  }, [defaultRangeMonths]);

  const fetchEvents = useCallback(async () => {
    // Audit issue #5 (client side). Parent pages may render TimelineRenderer
    // before the route param resolves, sending entityId="" through to a
    // request like `/api/timeline/<type>/?range_start=…`. That URL doesn't
    // match the server's `:entity_type/:entity_id` pattern (Hono resolves
    // empty segments to a non-match), so it 404'd via the global handler.
    // Bail out early — the parent will re-render with a real id.
    if (!entityId) {
      setEvents([]);
      setLoading(false);
      setError("");
      return;
    }
    try {
      const params = new URLSearchParams({
        range_start: rangeStart.toISOString(),
        range_end: rangeEnd.toISOString(),
      });
      const data = await authFetch<{ items: TimelineEvent[]; count: number }>(
        `/api/timeline/${entityType}/${entityId}?${params.toString()}`
      );
      setEvents(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load timeline";
      setError(msg);
      console.error("[TimelineRenderer]", err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchEvents();
    // refreshKey is intentionally a dep so external callers can force a re-fetch
  }, [fetchEvents, refreshKey]);

  const totalDays = daysBetween(rangeStart, rangeEnd);
  const pxPerDay = PX_PER_DAY[zoom];
  const totalWidth = Math.max(totalDays * pxPerDay, 600);

  // Tick generation: monthly for month/year, weekly for week, daily for day
  const ticks = useMemo(() => {
    const out: { label: string; offsetDays: number; isMajor: boolean }[] = [];
    const cursor = new Date(rangeStart);
    if (zoom === "year" || zoom === "month") {
      while (cursor < rangeEnd) {
        const offset = daysBetween(rangeStart, cursor);
        out.push({
          label: cursor.toLocaleDateString(undefined, {
            month: "short",
            year: "2-digit",
            timeZone: "UTC",
          }),
          offsetDays: offset,
          isMajor: cursor.getUTCMonth() === 0,
        });
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    } else if (zoom === "week") {
      const wk = startOfWeek(cursor);
      while (wk < rangeEnd) {
        const offset = daysBetween(rangeStart, wk);
        out.push({
          label: wk.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          }),
          offsetDays: offset,
          isMajor: wk.getUTCDate() <= 7,
        });
        wk.setUTCDate(wk.getUTCDate() + 7);
      }
    } else {
      // day zoom — show every 3rd day with label, all days as ticks
      const d = new Date(cursor);
      while (d < rangeEnd) {
        const offset = daysBetween(rangeStart, d);
        if (offset % 3 === 0) {
          out.push({
            label: d.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              timeZone: "UTC",
            }),
            offsetDays: offset,
            isMajor: d.getUTCDate() === 1,
          });
        }
        d.setUTCDate(d.getUTCDate() + 1);
      }
    }
    return out;
  }, [rangeStart, rangeEnd, zoom]);

  // Filter visible events
  const visibleEvents = useMemo(
    () => events.filter((e) => activeFilters.has(e.type)),
    [events, activeFilters]
  );

  // Lane-pack events to avoid overlap. Each lane is one row.
  // Greedy algorithm: for each event, find the first lane where its
  // [start, end] doesn't overlap with the lane's last placed event.
  const lanedEvents = useMemo(() => {
    const sorted = [...visibleEvents].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
    const lanes: number[] = []; // lane index -> end time (ms) of last event
    const result: Array<{ event: TimelineEvent; lane: number }> = [];

    for (const e of sorted) {
      const start = new Date(e.start_date).getTime();
      const endStr = e.end_date ?? e.start_date;
      const end = new Date(endStr).getTime() + (e.end_date ? 0 : 86_400_000); // 1-day point bars
      let placed = false;
      for (let i = 0; i < lanes.length; i++) {
        if (lanes[i] <= start) {
          lanes[i] = end;
          result.push({ event: e, lane: i });
          placed = true;
          break;
        }
      }
      if (!placed) {
        lanes.push(end);
        result.push({ event: e, lane: lanes.length - 1 });
      }
    }
    return { items: result, laneCount: Math.max(lanes.length, 1) };
  }, [visibleEvents]);

  const today = useMemo(() => {
    const t = new Date();
    t.setUTCHours(0, 0, 0, 0);
    return t;
  }, []);
  const todayOffsetDays =
    today >= rangeStart && today <= rangeEnd ? daysBetween(rangeStart, today) : null;

  const toggleFilter = (t: TimelineEventType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {/* Header: zoom + filter chips + new annotation */}
      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={zoom} onValueChange={(v) => setZoom(v as ZoomLevel)}>
          <TabsList>
            <TabsTrigger value="year">Year</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="day">Day</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {ALL_TYPES.map((t) => {
            const isActive = activeFilters.has(t);
            return (
              <button
                key={t}
                onClick={() => toggleFilter(t)}
                className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                  isActive
                    ? `${TYPE_BAR_CLASS[t]}`
                    : "bg-transparent text-muted-foreground border-border hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                }`}
                aria-pressed={isActive}
                aria-label={`Toggle ${TYPE_LABEL[t]} events`}
                style={{ minHeight: 32, minWidth: 32 }}
              >
                {TYPE_LABEL[t]}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {allowAnnotations && (
            <NewAnnotationButton
              entityType={entityType}
              entityId={entityId}
              onCreated={() => {
                fetchEvents();
                onAnnotationCreated?.();
              }}
            />
          )}
        </div>
      </div>

      {/* Timeline canvas */}
      {loading ? (
        <div className="border border-border rounded-md bg-white dark:bg-surface p-6 text-center text-sm text-muted-foreground">
          Loading timeline…
        </div>
      ) : error ? (
        <div className="border border-clay/30 rounded-md bg-clay/10 dark:bg-clay-dark/15 p-4 text-sm text-clay dark:text-clay-dark">
          {error}
        </div>
      ) : (
        <div className="border border-border rounded-md bg-surface dark:bg-surface-dark overflow-x-auto">
          <div
            style={{
              width: totalWidth,
              minWidth: "100%",
              position: "relative",
            }}
          >
            {/* Time axis header */}
            <div
              className="bg-surface-raised dark:bg-surface-raised-dark border-b border-border sticky top-0 z-10"
              style={{ height: HEADER_HEIGHT }}
            >
              {ticks.map((t, i) => (
                <div
                  key={i}
                  className={`absolute top-0 bottom-0 flex items-center px-1 text-2xs whitespace-nowrap ${
                    t.isMajor
                      ? "text-foreground font-medium border-l-2 border-border"
                      : "text-muted-foreground border-l border-border/50"
                  }`}
                  style={{ left: t.offsetDays * pxPerDay, height: HEADER_HEIGHT }}
                >
                  {t.label}
                </div>
              ))}
            </div>

            {/* Event lanes */}
            <div
              style={{
                position: "relative",
                height: Math.max(lanedEvents.laneCount * ROW_HEIGHT + 8, TRACK_HEIGHT),
              }}
            >
              {/* Vertical grid lines */}
              {ticks.map((t, i) => (
                <div
                  key={i}
                  className={`absolute top-0 bottom-0 ${
                    t.isMajor
                      ? "border-l border-border/50"
                      : "border-l border-border/20"
                  }`}
                  style={{ left: t.offsetDays * pxPerDay }}
                />
              ))}

              {/* Today marker */}
              {todayOffsetDays !== null && (
                <div
                  className="absolute top-0 bottom-0 border-l-2 border-dashed border-burnt-orange dark:border-burnt-orange-dark pointer-events-none z-20"
                  style={{ left: todayOffsetDays * pxPerDay }}
                  aria-label="Today"
                />
              )}

              {/* Event bars */}
              {lanedEvents.items.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                  No events in this range
                </div>
              ) : (
                lanedEvents.items.map(({ event: e, lane }) => {
                  const sow = new Date(e.start_date);
                  const endDate = e.end_date ? new Date(e.end_date) : new Date(sow.getTime() + 86_400_000);
                  const startOffset = Math.max(0, daysBetween(rangeStart, sow));
                  const lengthDays = Math.max(1, daysBetween(sow, endDate));
                  const top = lane * ROW_HEIGHT + 4;
                  return (
                    <button
                      key={e.id}
                      onClick={() => setSelectedEvent(e)}
                      title={`${e.title}\n${e.start_date.split("T")[0]}${e.end_date ? ` → ${e.end_date.split("T")[0]}` : ""}\n${TYPE_LABEL[e.type]}${e.is_projected ? " (projected)" : ""}`}
                      className={`absolute rounded-md border text-2xs px-2 flex items-center font-medium overflow-hidden whitespace-nowrap text-left hover:ring-2 hover:ring-offset-1 hover:ring-forest/40 dark:hover:ring-forest-dark/40 transition-all ${TYPE_BAR_CLASS[e.type]} ${
                        e.is_projected ? "opacity-50" : "opacity-100"
                      } ${selectedEvent?.id === e.id ? "ring-2 ring-offset-1 ring-forest dark:ring-forest-dark" : ""}`}
                      style={{
                        left: startOffset * pxPerDay,
                        width: Math.max(lengthDays * pxPerDay - 2, 24),
                        top,
                        height: ROW_HEIGHT - 8,
                      }}
                    >
                      {e.title}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected event detail */}
      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onAfterChange={fetchEvents}
        />
      )}

      {/* Summary footer */}
      <div className="text-xs text-muted-foreground flex justify-between">
        <span>
          {visibleEvents.length} event{visibleEvents.length === 1 ? "" : "s"} visible
          {events.length !== visibleEvents.length && ` (${events.length - visibleEvents.length} hidden by filters)`}
        </span>
        <span className="font-mono">
          {rangeStart.toISOString().split("T")[0]} → {rangeEnd.toISOString().split("T")[0]}
        </span>
      </div>
    </div>
  );
}

// ─── New annotation popover button ────────────────────────────────────────

function NewAnnotationButton({
  entityType,
  entityId,
  onCreated,
}: {
  entityType: TimelineEntityType;
  entityId: string;
  onCreated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [start, setStart] = useState(new Date().toISOString().split("T")[0]);
  const [end, setEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle("");
    setBody("");
    setStart(new Date().toISOString().split("T")[0]);
    setEnd("");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    setSubmitting(true);
    try {
      await authFetch("/api/timeline/annotations", {
        method: "POST",
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          start_date: new Date(start).toISOString(),
          end_date: end ? new Date(end).toISOString() : null,
          title: title.trim(),
          body: body.trim() || null,
        }),
      });
      toast.success("Annotation added");
      reset();
      setOpen(false);
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to add annotation";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Note
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="ann-title">Title *</Label>
            <Input
              id="ann-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Hit a deer"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="ann-body">Notes</Label>
            <Textarea
              id="ann-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ann-start">Date *</Label>
              <Input
                id="ann-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ann-end">Until (optional)</Label>
              <Input
                id="ann-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full" size="sm">
            {submitting ? "Saving…" : "Add note"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

// ─── Event detail panel (inline expansion) ────────────────────────────────

function EventDetailPanel({
  event,
  onClose,
  onAfterChange,
}: {
  event: TimelineEvent;
  onClose: () => void;
  onAfterChange: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (event.source_table !== "timeline_annotations") return;
    if (!confirm(`Delete annotation "${event.title}"?`)) return;
    setDeleting(true);
    try {
      await authFetch(`/api/timeline/annotations/${event.source_id}`, {
        method: "DELETE",
      });
      toast.success("Annotation deleted");
      onClose();
      onAfterChange();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to delete";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-border rounded-md bg-surface dark:bg-surface-dark p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={TYPE_BAR_CLASS[event.type]}>
              {TYPE_LABEL[event.type]}
            </Badge>
            {event.is_projected && (
              <Badge variant="outline" className="bg-surface-raised text-charcoal border-token-border dark:bg-surface-raised-dark dark:text-charcoal-dark dark:border-token-border-dark">
                Projected
              </Badge>
            )}
            {event.status && (
              <span className="text-xs text-muted-foreground">{event.status}</span>
            )}
          </div>
          <h4 className="font-medium text-foreground">{event.title}</h4>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {event.start_date.split("T")[0]}
            {event.end_date && ` → ${event.end_date.split("T")[0]}`}
          </div>
          {event.description && (
            <p className="text-sm text-muted-foreground mt-2">{event.description}</p>
          )}
          <p className="text-2xs text-muted-foreground mt-2 font-mono">
            source: {event.source_table}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          {event.source_table === "timeline_annotations" && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              className="text-clay border-clay/30 hover:bg-clay/10 dark:text-clay-dark dark:border-clay-dark/30 dark:hover:bg-clay-dark/15"
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
